/** Runtime validation for untrusted envelopes — SPEC §21.4 */
import { z } from "zod";

/**
 * Sender identity carried in-band so a one-way add can open the first message
 * (FR-CON-001 "exact public identity key" + FR-CON-003): the recipient creates
 * an UNVERIFIED contact from these keys. Authenticity is not taken on faith —
 * the seal only opens if these keys match the handshake transcript (AEAD
 * binding), and transportPubHex (when present) must equal the NIP-01 pubkey
 * whose Schnorr signature was already verified.
 */
export const SenderIdentitySchema = z
  .object({
    /** Sender messaging Ed25519 public key (64 hex). */
    messagingPubHex: z.string().regex(/^[0-9a-fA-F]{64}$/),
    /** Sender hybrid public X25519(32)‖ML-KEM-768 EK — 1216 bytes = 2432 hex. */
    hybridPubHex: z.string().regex(/^[0-9a-fA-F]{256,2432}$/),
    /** Sender BIP-340 x-only transport key (64 hex) — bound to the event pubkey. */
    transportPubHex: z.string().regex(/^[0-9a-fA-F]{64}$/).optional(),
    displayName: z.string().max(64).optional(),
  })
  .strict();

export type SenderIdentity = z.infer<typeof SenderIdentitySchema>;

export const TransportEnvelopeSchema = z
  .object({
    conversationId: z.string().min(16).max(128),
    sealedJson: z.string().min(2).max(100_000),
    senderDevice: z.string().min(8).max(64),
    handshakeJson: z.string().max(200_000).optional(),
    contentType: z.number().int().min(1).max(64).optional(),
    senderIdentity: SenderIdentitySchema.optional(),
    /** App-level control payloads (reactions/edits) travel inside sealedJson; this is advisory */
  })
  .strict();

export type TransportEnvelope = z.infer<typeof TransportEnvelopeSchema>;

export const RelayEventSchema = z.object({
  id: z.string().min(8).max(128),
  pubkey: z.string().max(128).optional(),
  created_at: z.number().int().optional(),
  kind: z.number().int(),
  tags: z.array(z.array(z.string())).max(32).optional(),
  content: z.string().max(120_000),
  sig: z.string().max(256).optional(),
});

export function parseTransportEnvelope(content: string): TransportEnvelope {
  const raw = JSON.parse(content) as unknown;
  return TransportEnvelopeSchema.parse(raw);
}
